import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  UserPlus,
  ArrowLeft,
  Mail,
  User,
  Building,
  AlertCircle,
  Check,
  RefreshCw,
  Globe,
} from "lucide-react";
import Header from "../../components/Header";
import {
  getAuthenticatedAxios,
  buildApiPath,
  API_ENDPOINTS,
} from "../../utils/api";

function InviteUser() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [error, setError] = useState(null);
  const [userCreated, setUserCreated] = useState(false);
  const [createdUserEmail, setCreatedUserEmail] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("en");

  const [formData, setFormData] = useState({
    email: "",
    full_name: "",
    company_id: "",
    is_active: true,
  });

  const [errors, setErrors] = useState({});

  const loadCompanies = useCallback(async () => {
    setLoadingCompanies(true);
    try {
      const axios = getAuthenticatedAxios();
      const response = await axios.get(
        buildApiPath(API_ENDPOINTS.ADMIN.COMPANIES),
      );
      const companiesData = response.data.items || [];

      // Only use companies with valid IDs
      const validCompanies = companiesData
        .filter((company) => {
          const id = company.id || company.company_id || company.CompanyId;
          return id && typeof id === "string" && id.length > 0;
        })
        .map((company) => ({
          company_id: company.id || company.company_id || company.CompanyId,
          name: company.name || company.Name || "Unnamed Company",
        }));

      if (validCompanies.length === 0) {
        setError("No companies available. Please create a company first.");
      } else {
        setCompanies(validCompanies);
        // Set first company as default if available
        setFormData((prev) =>
          prev.company_id
            ? prev
            : {
                ...prev,
                company_id: validCompanies[0].company_id,
              },
        );
      }
    } catch (err) {
      console.error("Failed to load companies:", err);
      setError("Failed to load companies. Please try again.");
    } finally {
      setLoadingCompanies(false);
    }
  }, []);

  // Load companies for selection
  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    // Name validation
    if (!formData.full_name) {
      newErrors.full_name = "Full name is required";
    }

    // Company validation
    if (!formData.company_id) {
      newErrors.company_id = "Company is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const axios = getAuthenticatedAxios();

      // Prepare request data for invitation
      const requestData = {
        email: formData.email.toLowerCase().trim(),
        full_name: formData.full_name.trim(),
        company_id: formData.company_id,
        role: "user", // Regular users are always "user" role
        is_active: formData.is_active,
        language: selectedLanguage,
      };

      await axios.post(buildApiPath(API_ENDPOINTS.ADMIN.INVITE_USER), requestData);

      setCreatedUserEmail(formData.email.toLowerCase().trim());
      setUserCreated(true);
    } catch (err) {
      console.error("Failed to create user:", err);

      // Handle different error types
      if (err.response?.status === 409) {
        setError("A user with this email already exists");
      } else if (err.response?.status === 400) {
        const detail = err.response.data?.detail;
        if (typeof detail === "string") {
          setError(detail);
        } else if (detail?.msg) {
          setError(detail.msg);
        } else {
          setError("Invalid data provided. Please check your inputs.");
        }
      } else if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError("Failed to create user. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Create another user
  const createAnother = () => {
    setUserCreated(false);
    setCreatedUserEmail("");
    setFormData({
      email: "",
      full_name: "",
      company_id: companies.length > 0 ? companies[0].company_id : "",
      is_active: true,
    });
    setErrors({});
    setError(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center">
            <button
              onClick={() => navigate("/admin/users")}
              className="mr-4 p-2 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <UserPlus className="h-8 w-8 mr-3 text-green-600" />
                Invite User
              </h1>
              <p className="mt-1 text-gray-600">
                Create a user account with an auto-generated password
              </p>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && !userCreated && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center">
            <AlertCircle className="h-5 w-5 mr-2" />
            {error}
          </div>
        )}

        {!userCreated ? (
          // Form for creating user
          <div className="bg-white shadow rounded-lg p-6">
            {/* Info Box */}
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-green-600 mt-0.5 mr-2 flex-shrink-0" />
                <div className="text-sm text-green-800">
                  <p className="font-semibold mb-1">User Invitation</p>
                  <p>
                    A secure password will be automatically generated for this
                    user.
                  </p>
                  <p className="mt-1">
                    After creation, you'll need to share the login credentials
                    with the user through a secure channel.
                  </p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Email Address *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                      errors.email ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder="user@example.com"
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>

              {/* Full Name */}
              <div>
                <label
                  htmlFor="full_name"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Full Name *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    id="full_name"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                      errors.full_name ? "border-red-500" : "border-gray-300"
                    }`}
                    placeholder="John Doe"
                  />
                </div>
                {errors.full_name && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.full_name}
                  </p>
                )}
              </div>

              {/* Company */}
              <div>
                <label
                  htmlFor="company_id"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Company *
                </label>
                <div className="relative">
                  <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <select
                    id="company_id"
                    name="company_id"
                    value={formData.company_id}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                      errors.company_id ? "border-red-500" : "border-gray-300"
                    }`}
                  >
                    <option value="">Select a company</option>
                    {companies.map((company) => (
                      <option
                        key={company.company_id}
                        value={company.company_id}
                      >
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>
                {errors.company_id && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.company_id}
                  </p>
                )}
              </div>

              {/* Language Selector */}
              <div>
                <label
                  htmlFor="language"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Email Language
                </label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <select
                    id="language"
                    value={selectedLanguage}
                    onChange={(e) => setSelectedLanguage(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="en">English</option>
                    <option value="jp">日本語 (Japanese)</option>
                  </select>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Language for the invitation email
                </p>
              </div>

              {/* Active Status */}
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Active</span>
                </label>
                <p className="mt-1 text-xs text-gray-500">
                  Inactive users cannot log in to the system
                </p>
              </div>

              {/* Submit Buttons */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => navigate("/admin/users")}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || loadingCompanies}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <UserPlus className="h-4 w-4" />
                      Create User
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        ) : (
          // Success screen
          <div className="bg-white shadow rounded-lg p-6">
            <div className="text-center mb-6">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <Check className="h-6 w-6 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">
                User Invited Successfully!
              </h2>
              <p className="mt-2 text-gray-600">
                An invitation email has been sent with instructions to set up
                their password.
              </p>
            </div>

            <div className="bg-green-50 rounded-lg p-6">
              <div className="flex items-start">
                <Mail className="h-5 w-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-green-900">
                    Invitation sent to: {createdUserEmail}
                  </p>
                  <p className="mt-1 text-sm text-green-700">
                    The user will receive an email in{" "}
                    {selectedLanguage === "jp" ? "Japanese" : "English"} with a
                    link to set their password.
                  </p>
                  <p className="mt-2 text-sm text-green-700">
                    The link will expire in 48 hours.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-semibold">What happens next?</p>
                  <ul className="mt-1 list-disc list-inside space-y-1">
                    <li>User receives email with password setup link</li>
                    <li>User clicks link and creates their own password</li>
                    <li>
                      User can then log in with their email and new password
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between mt-6">
              <button
                onClick={createAnother}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Create Another User
              </button>
              <button
                onClick={() => navigate("/admin/users")}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to User Management
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default InviteUser;
